'use client';

import { Member } from '@/app/(intakeFormV2)/intake-v2/types';
import { cn } from '@/lib/ui/cn';
import { useCallback } from 'react';
import { MemberCard } from '../steps/MemberCard';

type Props = {
  value: Member[];
  onChange: (members: Member[]) => void;
  errors?: Record<string, string>;
  className?: string;
};

export const MemberDetailsForm: React.FC<Props> = ({
  value,
  onChange,
  errors = {},
  className,
}) => {
  const handleMemberUpdate = useCallback(
    (index: number, field: keyof Member, fieldValue: string) => {
      const updated = value.map((member, i) =>
        i === index ? { ...member, [field]: fieldValue } : member
      );
      onChange(updated);
    },
    [onChange, value]
  );

  const handleAddMember = useCallback(
    () =>
      onChange([
        ...value,
        {
          id: crypto.randomUUID(),
          contact_id: crypto.randomUUID(),
          first_name: '',
          last_name: '',
          date_of_birth: '',
          isPrimary: false,
          plan: 'Signature',
        },
      ]),
    [onChange, value]
  );

  const handleRemoveMember = useCallback(
    (index: number) => onChange(value.filter((_, i) => i !== index)),
    [onChange, value]
  );

  return (
    <div className={cn('flex flex-col gap-[40px]', className)}>
      <div className='flex flex-row items-center justify-center gap-4 rounded-[18px] border border-[#F1E6D6] bg-[#F7F5EF] p-4'>
        <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#BBBBBB]'>
          <svg
            width='14'
            height='14'
            viewBox='0 0 14 14'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M7 0.5V13.5M9.5 3H5.75C5.15326 3 4.58097 3.23705 4.15901 3.65901C3.73705 4.08097 3.5 4.65326 3.5 5.25C3.5 5.84674 3.73705 6.41903 4.15901 6.84099C4.58097 7.26295 5.15326 7.5 5.75 7.5H8.25C8.84674 7.5 9.41903 7.73705 9.84099 8.15901C10.2629 8.58097 10.5 9.15326 10.5 9.75C10.5 10.3467 10.2629 10.919 9.84099 11.341C9.41903 11.7629 8.84674 12 8.25 12H3.5'
              stroke='#292722'
              strokeWidth='1.2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>

        <p className='flex-1 font-sans text-[21px] leading-[130%] font-normal text-[#292722]'>
          Save{' '}
          <span className='font-bold'>
            $1,000 or more per additional member
          </span>
          , saving more as your account grows.
        </p>
      </div>

      {value.map((member, index) => (
        <MemberCard
          key={member.id}
          member={member}
          index={index}
          errors={errors}
          onUpdate={(field, fieldValue) =>
            handleMemberUpdate(index, field, fieldValue)
          }
          onRemove={index > 0 ? () => handleRemoveMember(index) : undefined}
        />
      ))}

      <button
        type='button'
        onClick={handleAddMember}
        className='border-sh-gray hover:border-sh-dark-gray flex h-[56px] w-full cursor-pointer items-center gap-2 rounded-[40px] border-2 bg-white p-4 transition-colors'
      >
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M9 1V17M1 9H17'
            stroke='#000000'
            strokeWidth='2'
            strokeLinecap='round'
          />
        </svg>
        <span className='flex-1 text-left font-sans text-[18px] leading-[130%] font-normal text-[#292722]'>
          Add Member
        </span>
      </button>
    </div>
  );
};
